import ContentCopy from "@mui/icons-material/ContentCopy";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import FileDownload from "@mui/icons-material/FileDownload";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import {
  Alert,
  Box,
  Button,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { isEqual } from "lodash-es";
import { useCallback, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router";
import { useDialog } from "../../utils/hooks/use-dialog";
import { usePopover } from "../../utils/hooks/use-popover";
import { useDesignHuddleProjectClone } from "../design-huddle/use-design-huddle-project-clone";
import { ArchiveConfirmation } from "./archive-confirmation";
import { ExportMenu } from "./asset-export-menu";
import { AssetPreviewModalTitle } from "./asset-preview-modal-title";
import { AssetTagManager } from "./asset-tag-manager";
import { AssetFolder } from "./components/asset-folder";
import { VideoAssetPreview } from "./components/cloudflare-stream-iframe";
import { useDeleteAssetCallback } from "./hooks/use-delete-asset-callback";
import { useCreateAssetCallback } from "./hooks/use-upsert-asset";

import type { Asset } from "./server/digital-asset-service";
import type { FC } from "react";

export const DesignHuddleAssetPreviewModal: FC<{
  open: boolean;
  onClose: () => void;
  asset: Asset;
  onChange?: () => void;
  onArchived?: () => void;
  onFolderChange: () => void;
  folders_feature_flag: boolean;
}> = ({ asset, open, onClose, onChange, onArchived, onFolderChange, folders_feature_flag }) => {
  const navigate = useNavigate();

  const [copying, setCopying] = useState(false);
  const createAsset = useCreateAssetCallback();

  const [filename, setFilename] = useState(asset.title);
  const [filenameUpdated, setFilenameUpdated] = useState<boolean>(false);
  const [localTags, setLocalTags] = useState<string[]>(asset.tags);

  const handleCloseDialog = useCallback(() => {
    onClose();
    if (filenameUpdated || !isEqual(localTags, asset.tags)) onChange?.();
    setFilenameUpdated(false);
  }, [asset.tags, filenameUpdated, localTags, onChange, onClose]);

  const cloneProject = useDesignHuddleProjectClone();

  const handleCopy = useCallback(async () => {
    if (copying) return;

    setCopying(true);

    const loadingToast = toast.custom(<Alert severity="info">Creating copy...</Alert>);

    if (!asset.project_id) return;
    try {
      const projectId = await cloneProject(asset.project_id, asset.page_id);
      await createAsset({
        projectId,
        contentType: asset.content_type,
        thumbnailUrl: asset.thumbnail_url,
        publicUrl: asset.public_url,
        pageId: asset.page_id,
        title: `Copy of ${asset.title}`,
        pageCount: asset.page_count,
        folderId: asset.folder_id,
      });

      toast.dismiss(loadingToast);
      toast.success(`Copy of "${asset.title}" created`);
      onChange?.();
    } catch (error) {
      console.error(error);
      toast.dismiss(loadingToast);
      toast.error("Failed to create copy. Please try again later");
    } finally {
      setCopying(false);
    }
  }, [asset, cloneProject, copying, createAsset, onChange]);

  const archiveConfirmation = useDialog();
  const deleteAsset = useDeleteAssetCallback();

  const handleConfirmArchive = useCallback(async () => {
    archiveConfirmation.handleClose();

    try {
      await deleteAsset(asset.id);
      toast.success(`'${asset.title}' archived`);
      onArchived?.();
    } catch (error) {
      console.error(error);
      toast.error("Failed to archive asset");
    }
  }, [asset, deleteAsset, archiveConfirmation, onArchived]);

  const [anchorRef, exportMenuController] = usePopover<HTMLButtonElement>();

  const isVideo = asset.content_type === "Video";

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        slotProps={{ paper: { sx: { maxWidth: 650 } } }}
      >
        <DialogTitle display="flex" justifyContent="space-between" component="div">
          <AssetPreviewModalTitle
            asset={asset}
            filename={filename}
            setFilename={setFilename}
            setFilenameUpdated={setFilenameUpdated}
            handleCloseDialog={handleCloseDialog}
          />
        </DialogTitle>
        <DialogContent>
          <Stack display="flex" alignItems="center" justifyContent="center" mt={4} gap={2} mb={2}>
            {isVideo ? (
              <VideoAssetPreview asset={asset} />
            ) : (
              asset.thumbnail_url && <CardMedia src={asset.thumbnail_url} component="img" />
            )}
          </Stack>
          {folders_feature_flag && (
            <Box mb={2}>
              <AssetFolder asset={asset} onFolderChange={onFolderChange} />
            </Box>
          )}
          <AssetTagManager asset={asset} localTags={localTags} setLocalTags={setLocalTags} />
        </DialogContent>
        <DialogActions sx={{ justifyItems: "right", px: 2, mb: 2 }}>
          <Button
            startIcon={<LinkOutlined />}
            data-analytics-id="content-library-preview-get-embed-link"
            onClick={() => {
              if (!asset.public_url) {
                toast.error("Unable to get Link");
                return;
              }

              void navigator.clipboard.writeText(asset.public_url);
              toast.success("Link copied to clipboard");
            }}
          >
            Get Embed Link
          </Button>
          <Button
            ref={anchorRef}
            onClick={exportMenuController.handleOpen}
            startIcon={<FileDownload />}
            data-analytics-id="content-library-preview-export"
          >
            Export
          </Button>
          <Button
            startIcon={<ContentCopy data-testid="ContentCopyIcon" />}
            data-analytics-id="content-library-preview-duplicate"
            onClick={() => void handleCopy()}
            loading={copying}
          >
            Duplicate
          </Button>
          <Button
            startIcon={<Edit />}
            data-analytics-id="content-library-preview-edit"
            onClick={() => void navigate(`/content-library/${asset.id}`)}
          >
            Edit
          </Button>
          <Button
            startIcon={<DeleteOutline data-testid="DeleteOutlineIcon" />}
            data-analytics-id="content-library-preview-archive"
            onClick={archiveConfirmation.handleOpen}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <ArchiveConfirmation
        asset={asset}
        open={archiveConfirmation.open}
        onClose={archiveConfirmation.handleClose}
        onConfirm={() => void handleConfirmArchive()}
      />
      <ExportMenu anchorRef={anchorRef} menuController={exportMenuController} asset={asset} />
    </>
  );
};
